import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';

const SELECTED_EVENT_STORAGE_KEY = 'adminSelectedProductId';

const AdminPage = ({ adminPassword, onLogout }) => {
    const [css] = useStyletron();
    const [products, setProducts] = useState([]);
    const [scanCount, setScanCount] = useState(0);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [inventoryInputs, setInventoryInputs] = useState({});
    const [sheetValue, setSheetValue] = useState('');
    const [worksheetRange, setWorksheetRange] = useState('');
    const [sheetTabName, setSheetTabName] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);

    const headers = useMemo(
        () => ({
            'Content-Type': 'application/json',
            'x-admin-password': adminPassword,
        }),
        [adminPassword]
    );

    const loadProducts = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/storefront/products`);
        if (!response.ok) {
            throw new Error('Failed to load products.');
        }

        const data = await response.json();
        const nextProducts = data || [];
        setProducts(nextProducts);
        const params = new URLSearchParams(window.location.search);
        const requestedProductId = params.get('event');
        const storedProductId = localStorage.getItem(SELECTED_EVENT_STORAGE_KEY);
        const preferredProductId = [requestedProductId, storedProductId].find((value) => value && nextProducts.some((product) => product.id === value));
        setSelectedProductId((current) => {
            const currentIsValid = current && nextProducts.some((product) => product.id === current);
            const nextProductId = currentIsValid ? current : (preferredProductId || nextProducts[0]?.id || '');
            return nextProductId;
        });
        setInventoryInputs(
            nextProducts.reduce((accumulator, product) => ({
                ...accumulator,
                [product.id]: String(product.inventoryCount ?? 0),
            }), {})
        );
    }, []);

    const selectedProduct = useMemo(
        () => products.find((product) => product.id === selectedProductId) || null,
        [products, selectedProductId]
    );

    const loadSheetTarget = useCallback(async () => {
        if (!selectedProductId) {
            setSheetValue('');
            setWorksheetRange('');
            setSheetTabName('');
            return;
        }

        const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/admin/google-sheet`);
        url.searchParams.set('productId', selectedProductId);
        if (selectedProduct?.title) {
            url.searchParams.set('productName', selectedProduct.title);
        }

        const response = await fetch(url.toString(), {
            headers,
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to load Google Sheet target.');
        }

        const data = await response.json();
        setSheetValue(data.spreadsheetId || '');
        setWorksheetRange(data.worksheetRange || '');
        setSheetTabName(data.tabName || '');
        if (data.created) {
            setStatus(`Created Google Sheet tab: ${data.tabName}.`);
        }
    }, [headers, selectedProductId, selectedProduct]);

    const loadScanCount = useCallback(async () => {
        if (!selectedProductId) {
            setScanCount(0);
            return;
        }

        const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/admin/scan-stats`);
        url.searchParams.set('productId', selectedProductId);

        const scopedResponse = await fetch(url.toString(), {
            headers,
        });

        if (!scopedResponse.ok) {
            const data = await scopedResponse.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to load admitted count.');
        }

        const data = await scopedResponse.json();
        setScanCount(Number.isFinite(Number(data.successfulScans)) ? Number(data.successfulScans) : 0);
    }, [headers, selectedProductId]);

    useEffect(() => {
        let isActive = true;

        const load = async () => {
            try {
                setLoading(true);
                await loadProducts();
            } catch (error) {
                if (isActive) {
                    setStatus(error.message || 'Failed to load admin data.');
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isActive = false;
        };
    }, [loadProducts]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            loadScanCount().catch(() => undefined);
        }, 15000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [loadScanCount]);

    useEffect(() => {
        if (!selectedProductId) {
            return;
        }

        localStorage.setItem(SELECTED_EVENT_STORAGE_KEY, selectedProductId);

        let isActive = true;
        const loadSelectedEventData = async () => {
            try {
                setLoading(true);
                await Promise.all([loadSheetTarget(), loadScanCount()]);
            } catch (error) {
                if (isActive) {
                    setStatus(error.message || 'Failed to load selected event data.');
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        loadSelectedEventData();

        return () => {
            isActive = false;
        };
    }, [selectedProductId, loadSheetTarget, loadScanCount]);

    const eventOptions = useMemo(() => (
        products.map((product) => ({
            value: product.id,
            label: [product.title, product.date, product.venue].filter(Boolean).join(' | '),
        }))
    ), [products]);

    const handleInventoryChange = (productId, value) => {
        setInventoryInputs((current) => ({
            ...current,
            [productId]: value,
        }));
    };

    const handleSetInventory = async (productId) => {
        const total = Number(inventoryInputs[productId]);
        if (!Number.isFinite(total) || total < 0) {
            setStatus('Enter zero or a positive inventory total.');
            return;
        }

        try {
            setStatus('');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/inventory/${productId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ total }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to update inventory.');
            }

            setProducts((current) => current.map((product) => (
                product.id === productId
                    ? { ...product, inventoryCount: data.inventoryCount, soldOut: data.inventoryCount === 0, lowInventory: data.inventoryCount > 0 && data.inventoryCount <= 10 }
                    : product
            )));
            setInventoryInputs((current) => ({
                ...current,
                [productId]: String(data.inventoryCount),
            }));
            setStatus('Inventory total updated.');
        } catch (error) {
            setStatus(error.message || 'Failed to update inventory.');
        }
    };

    const handleCreateSheetTab = async (event) => {
        event.preventDefault();

        if (!selectedProductId) {
            setStatus('Select an event before creating its Google Sheet tab.');
            return;
        }

        try {
            setStatus('');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/google-sheet`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ productId: selectedProductId, productName: selectedProduct?.title || '' }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to create Google Sheet tab.');
            }

            setSheetValue(data.spreadsheetId || '');
            setWorksheetRange(data.worksheetRange || '');
            setSheetTabName(data.tabName || '');
            setStatus(data.created ? `Created Google Sheet tab: ${data.tabName}.` : `Google Sheet tab ready: ${data.tabName}.`);
        } catch (error) {
            setStatus(error.message || 'Failed to create Google Sheet tab.');
        }
    };

    const handleResendTicket = async (event) => {
        event.preventDefault();

        try {
            setStatus('');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/tickets/resend`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ email: resendEmail.trim() }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to resend ticket email.');
            }

            setStatus(`Resent ${data.sentCount || 0} ticket email${data.sentCount === 1 ? '' : 's'}.`);
            setResendEmail('');
        } catch (error) {
            setStatus(error.message || 'Failed to resend ticket email.');
        }
    };

    const pageStyle = css({
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #d23c1b 0%, #BA5A31 48.55%, #E59F71 100%)',
        padding: '2rem 1rem 4rem',
    });

    const shellStyle = css({
        maxWidth: '960px',
        margin: '0 auto',
        display: 'grid',
        gap: '1rem',
    });

    const cardStyle = css({
        background: '#fff',
        borderRadius: '12px',
        padding: '1.25rem',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
    });

    const titleStyle = css({
        fontFamily: 'Impact, sans-serif',
        fontSize: '2.25rem',
        margin: '0 0 0.5rem',
        color: '#2b170f',
    });

    const sectionTitleStyle = css({
        margin: '0 0 0.75rem',
        color: '#2b170f',
    });

    const rowStyle = css({
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 120px 120px',
        gap: '0.75rem',
        alignItems: 'center',
        padding: '0.75rem 0',
        borderBottom: '1px solid #eee',
    });

    const inputStyle = {
        width: '100%',
        padding: '0.75rem',
        fontSize: '1rem',
        borderRadius: '6px',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
    };

    const buttonStyle = {
        padding: '0.75rem 1rem',
        fontSize: '0.95rem',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#d23c1b',
        color: '#fff',
        cursor: 'pointer',
    };

    const subtleButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#6b301d',
    };

    return (
        <div className={pageStyle}>
            <div className={shellStyle}>
                <div className={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                        <div>
                            <h1 className={titleStyle}>APT 300 Admin</h1>
                            <p style={{ margin: 0, color: '#6b5b53' }}>Pick an event once, then manage admissions, sheet tabs, and inventory for that event.</p>
                        </div>
                        <button type="button" style={subtleButtonStyle} onClick={onLogout}>Log out</button>
                    </div>
                    {status ? <p style={{ marginTop: '1rem', color: status.toLowerCase().includes('failed') || status.toLowerCase().includes('error') ? '#b42318' : '#027a48' }}>{status}</p> : null}
                </div>

                <div className={cardStyle}>
                    <h2 className={sectionTitleStyle}>Selected Event</h2>
                    <p style={{ marginTop: '-0.35rem', color: '#6b5b53' }}>This controls every admin section below.</p>
                    <select value={selectedProductId} onChange={(event) => setSelectedProductId(event.target.value)} style={inputStyle}>
                        {eventOptions.length === 0 ? <option value="">Loading events...</option> : null}
                        {eventOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {selectedProduct ? (
                        <div style={{ marginTop: '0.9rem', color: '#6b5b53', fontSize: '0.95rem' }}>
                            <strong style={{ color: '#2b170f' }}>{selectedProduct.title}</strong>
                            <div>{[selectedProduct.date, selectedProduct.time, selectedProduct.venue, selectedProduct.city].filter(Boolean).join(' | ')}</div>
                            <div>Product ID: {selectedProduct.id}</div>
                        </div>
                    ) : null}
                </div>

                <div className={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                        <div>
                            <h2 className={sectionTitleStyle} style={{ marginBottom: '0.35rem' }}>Admissions</h2>
                            <p style={{ margin: 0, color: '#6b5b53' }}>Successful ticket admits for the selected event.</p>
                        </div>
                        <button type="button" style={subtleButtonStyle} onClick={() => loadScanCount().catch((error) => setStatus(error.message || 'Failed to load admitted count.'))}>Refresh Count</button>
                    </div>
                    <div style={{ marginTop: '0.9rem', fontSize: '2.4rem', fontWeight: 800, color: '#2b170f', lineHeight: 1 }}>{scanCount}</div>
                </div>

                <div className={cardStyle}>
                    <h2 className={sectionTitleStyle}>Google Sheets</h2>
                    <p style={{ marginTop: '-0.35rem', color: '#6b5b53' }}>Uses the existing master spreadsheet. A tab is created automatically for the selected event.</p>
                    <form onSubmit={handleCreateSheetTab}>
                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.35rem', color: '#2b170f' }}>Master spreadsheet ID</label>
                        <input
                            type="text"
                            value={sheetValue}
                            readOnly
                            placeholder="Master Google Sheet not configured"
                            style={{ ...inputStyle, backgroundColor: '#f8f4f1' }}
                        />
                        <div style={{ height: '0.75rem' }} />
                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.35rem', color: '#2b170f' }}>Auto-created tab</label>
                        <input
                            type="text"
                            value={sheetTabName || worksheetRange}
                            readOnly
                            placeholder="Select an event to create/load its tab"
                            style={{ ...inputStyle, backgroundColor: '#f8f4f1' }}
                        />
                        <div style={{ marginTop: '0.75rem' }}>
                            <button type="submit" style={buttonStyle}>Create / Refresh Event Tab</button>
                        </div>
                    </form>
                </div>

                <div className={cardStyle}>
                    <h2 className={sectionTitleStyle}>Resend Ticket Email</h2>
                    <form onSubmit={handleResendTicket}>
                        <input
                            type="email"
                            value={resendEmail}
                            onChange={(event) => setResendEmail(event.target.value)}
                            placeholder="Enter customer email"
                            style={inputStyle}
                        />
                        <div style={{ marginTop: '0.75rem' }}>
                            <button type="submit" style={buttonStyle}>Resend Email</button>
                        </div>
                    </form>
                </div>

                <div className={cardStyle}>
                    <h2 className={sectionTitleStyle}>Inventory</h2>
                    <p style={{ marginTop: '-0.35rem', color: '#6b5b53' }}>Inventory is shown for the selected event only.</p>
                    {loading ? <p>Loading admin data...</p> : null}
                    {!loading && !selectedProduct ? <p>Select an event to manage inventory.</p> : null}
                    {!loading && selectedProduct ? (
                        <div className={rowStyle} key={selectedProduct.id}>
                            <div>
                                <div style={{ fontWeight: 700 }}>{selectedProduct.title}</div>
                                <div style={{ color: '#6b5b53', fontSize: '0.95rem' }}>Product ID: {selectedProduct.id} | Current: {selectedProduct.inventoryCount ?? 0}</div>
                            </div>
                            <input
                                type="number"
                                min="0"
                                placeholder="Total left"
                                value={inventoryInputs[selectedProduct.id] || ''}
                                onChange={(event) => handleInventoryChange(selectedProduct.id, event.target.value)}
                                style={inputStyle}
                            />
                            <button type="button" style={buttonStyle} onClick={() => handleSetInventory(selectedProduct.id)}>Save</button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AdminPage;